import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal, InjectionToken, Optional, Inject } from '@angular/core';
import { Observable, throwError, lastValueFrom, tap, map, catchError } from 'rxjs';

/**
 * Interface for HTTP error responses
 */
export interface HttpCoreError {
  error_message: string;
  explanation: string;
  status?: number;
  code?: string;
}

/**
 * Interface for deleted data response
 */
export interface DeletedData {
  acknowledged: boolean;
  deletedCount: number;
}

/**
 * Configuration for HttpCoreService
 */
export interface HttpCoreConfig {
  primaryUrl: string;
  secondaryUrl?: string;
  defaultHost?: 'primary' | 'secondary';
  errorHandler?: (error: HttpErrorResponse) => void;
}

/**
 * Injection token for HttpCoreService configuration
 */
export const HTTP_CORE_CONFIG = new InjectionToken<HttpCoreConfig>('HTTP_CORE_CONFIG');

/**
 * Modern HTTP service for Angular applications
 * Provides methods for making HTTP requests with proper error handling and response transformation
 */
@Injectable({
  providedIn: 'root',
})
export class HttpCoreService {
  private httpClient = inject(HttpClient);

  // Use signals for reactive state
  private isLoading = signal<boolean>(false);
  private lastError = signal<HttpCoreError | null>(null);

  // Configuration
  private config: HttpCoreConfig;

  constructor(@Optional() @Inject(HTTP_CORE_CONFIG) config: HttpCoreConfig | null) {
    if (config && config.primaryUrl) {
      this.config = {
        defaultHost: 'primary',
        ...config,
      };
    } else {
      // Initialize with a placeholder - this will be set properly with setConfig
      this.config = {
        primaryUrl: '',
        defaultHost: 'primary',
      };

      if (!config) {
        console.warn('HttpCoreService: No configuration provided. Use setConfig to configure the service.');
      } else if (!config.primaryUrl) {
        console.warn('HttpCoreService: primaryUrl is required in configuration. Use setConfig to set it.');
      }
    }
  }

  /**
   * Set the configuration for the service
   * This allows for manual configuration when not using dependency injection
   */
  setConfig(config: HttpCoreConfig): void {
    this.config = {
      ...this.config,
      ...config,
    };
  }

  /**
   * Get the loading state as a signal
   */
  public loading() {
    return this.isLoading;
  }

  /**
   * Get the last error as a signal
   */
  public error() {
    return this.lastError;
  }

  /**
   * Make a POST request to the specified service
   * @param service The service endpoint
   * @param data The data to send
   * @param host The host to use (primary or secondary)
   * @returns A promise with the response
   */
  public async post<T = any>(service: string, data: any, host = this.config.defaultHost): Promise<T> {
    this.isLoading.set(true);

    try {
      const url = this.getHostUrl(host) + '/' + service;

      const dataPlain = this.toPlainObject(data);

      const response$ = this.httpClient.post<T>(url, dataPlain).pipe(tap(this.checkResponseStatus.bind(this)), catchError(this.handleRequestError.bind(this)));

      const result = await lastValueFrom<T>(response$);
      this.isLoading.set(false);
      return result;
    } catch (error) {
      this.isLoading.set(false);
      throw error;
    }
  }

  /**
   * Make a POST request to a specific URL
   * @param baseUrl The base URL
   * @param service The service endpoint
   * @param data The data to send
   * @returns A promise with the response
   */
  public async postToUrl<T = any>(baseUrl: string, service: string, data: any): Promise<T> {
    this.isLoading.set(true);

    try {
      const url = `${baseUrl}/${service}`;
      const dataPlain = this.toPlainObject(data);

      const response$ = this.httpClient.post<T>(url, dataPlain).pipe(tap(this.checkResponseStatus.bind(this)), catchError(this.handleRequestError.bind(this)));

      const result = await lastValueFrom<T>(response$);
      this.isLoading.set(false);
      return result;
    } catch (error) {
      this.isLoading.set(false);
      throw error;
    }
  }

  /**
   * Make a PUT request to the specified service
   * @param service The service endpoint
   * @param data The data to send
   * @param host The host to use (primary or secondary)
   * @returns A promise with the response
   */
  public async put<T = any>(service: string, data: any, host = this.config.defaultHost): Promise<T> {
    this.isLoading.set(true);

    try {
      const url = this.getHostUrl(host) + '/' + service;
      const dataPlain = this.toPlainObject(data);

      const response$ = this.httpClient.put<T>(url, dataPlain).pipe(catchError(this.handleRequestError.bind(this)));

      const result = await lastValueFrom<T>(response$);
      this.isLoading.set(false);
      return result;
    } catch (error) {
      this.isLoading.set(false);
      throw error;
    }
  }

  /**
   * Make a PUT request to a specific URL
   * @param baseUrl The base URL
   * @param service The service endpoint
   * @param data The data to send
   * @returns A promise with the response
   */
  public async putToUrl<T = any>(baseUrl: string, service: string, data: any): Promise<T> {
    this.isLoading.set(true);

    try {
      const url = `${baseUrl}/${service}`;
      const dataPlain = this.toPlainObject(data);

      const response$ = this.httpClient.put<T>(url, dataPlain).pipe(catchError(this.handleRequestError.bind(this)));

      const result = await lastValueFrom<T>(response$);
      this.isLoading.set(false);
      return result;
    } catch (error) {
      this.isLoading.set(false);
      throw error;
    }
  }

  /**
   * Make a GET request to the specified service
   * @param service The service endpoint
   * @param host The host to use (primary or secondary)
   * @param skipErrorHandling Whether to skip error handling
   * @returns A promise with the response
   */
  public async get<T = any>(service: string, host = this.config.defaultHost, skipErrorHandling = false): Promise<T> {
    this.isLoading.set(true);

    try {
      const url = this.getHostUrl(host) + '/' + service;

      const response$ = this.httpClient.get<T>(url).pipe(
        catchError((err) => {
          if (!skipErrorHandling) {
            return this.handleRequestError(err);
          }
          return throwError(() => err);
        }),
      );

      const result = await lastValueFrom<T>(response$);
      this.isLoading.set(false);
      return result;
    } catch (error) {
      this.isLoading.set(false);
      throw error;
    }
  }

  /**
   * Make a GET request to a specific URL
   * @param baseUrl The base URL
   * @param service The service endpoint
   * @param skipErrorHandling Whether to skip error handling
   * @returns A promise with the response
   */
  public async getFromUrl<T = any>(baseUrl: string, service: string, skipErrorHandling = false): Promise<T> {
    this.isLoading.set(true);

    try {
      const url = `${baseUrl}/${service}`;

      const response$ = this.httpClient.get<T>(url).pipe(
        catchError((err) => {
          if (!skipErrorHandling) {
            return this.handleRequestError(err);
          }
          return throwError(() => err);
        }),
      );

      const result = await lastValueFrom<T>(response$);
      this.isLoading.set(false);
      return result;
    } catch (error) {
      this.isLoading.set(false);
      throw error;
    }
  }

  /**
   * Make a DELETE request to the specified service
   * @param service The service endpoint
   * @param host The host to use (primary or secondary)
   * @returns A promise with the response
   */
  public async delete<T = DeletedData>(service: string, host = this.config.defaultHost): Promise<T> {
    this.isLoading.set(true);

    try {
      const url = this.getHostUrl(host) + '/' + service;

      const response$ = this.httpClient.delete<T>(url).pipe(catchError(this.handleRequestError.bind(this)));

      const result = await lastValueFrom<T>(response$);
      this.isLoading.set(false);
      return result;
    } catch (error) {
      this.isLoading.set(false);
      throw error;
    }
  }

  /**
   * Make a DELETE request to a specific URL
   * @param baseUrl The base URL
   * @param service The service endpoint
   * @returns A promise with the response
   */
  public async deleteFromUrl<T = DeletedData>(baseUrl: string, service: string): Promise<T> {
    this.isLoading.set(true);

    try {
      const url = `${baseUrl}/${service}`;

      const response$ = this.httpClient.delete<T>(url).pipe(catchError(this.handleRequestError.bind(this)));

      const result = await lastValueFrom<T>(response$);
      this.isLoading.set(false);
      return result;
    } catch (error) {
      this.isLoading.set(false);
      throw error;
    }
  }

  /**
   * Get an observable for a GET request
   * @param service The service endpoint
   * @param host The host to use (primary or secondary)
   * @returns An observable with the response
   */
  public getObservable<T = any>(service: string, host = this.config.defaultHost): Observable<T> {
    const url = this.getHostUrl(host) + '/' + service;

    return this.httpClient.get<T>(url).pipe(catchError(this.handleRequestError.bind(this)));
  }

  /**
   * Get an observable for a POST request
   * @param service The service endpoint
   * @param data The data to send
   * @param skipErrorHandling Whether to skip error handling
   * @param host The host to use (primary or secondary)
   * @returns An observable with the response
   */
  public postObservable<T = any>(service: string, data: any, skipErrorHandling = false, host = this.config.defaultHost): Observable<T> {
    const url = this.getHostUrl(host) + '/' + service;
    const dataPlain = this.toPlainObject(data);

    return this.httpClient.post<T>(url, dataPlain).pipe(
      catchError((err) => {
        if (!skipErrorHandling) {
          return this.handleRequestError(err);
        }
        return throwError(() => err);
      }),
    );
  }

  /**
   * Get an observable for a DELETE request
   * @param service The service endpoint
   * @param host The host to use (primary or secondary)
   * @returns An observable with the response
   */
  public deleteObservable<T = DeletedData>(service: string, host = this.config.defaultHost): Observable<T> {
    const url = this.getHostUrl(host) + '/' + service;

    return this.httpClient.delete<T>(url).pipe(catchError(this.handleRequestError.bind(this)));
  }

  /**
   * Upload a file to the specified service
   * @param service The service endpoint
   * @param file The file to upload
   * @param metadata Optional metadata to send with the file
   * @param host The host to use (primary or secondary)
   * @returns A promise with the response
   */
  public async uploadFile<T = any>(service: string, file: Blob, metadata: any = null, host = this.config.defaultHost): Promise<T> {
    this.isLoading.set(true);

    try {
      const url = this.getHostUrl(host) + '/' + service;
      const formData = new FormData();
      formData.append('file', file);

      let headers = {};
      if (metadata) {
        headers = { metadata: JSON.stringify(metadata) };
      }

      const response$ = this.httpClient.post<T>(url, formData, { headers });
      const result = await lastValueFrom<T>(response$);
      this.isLoading.set(false);
      return result;
    } catch (error) {
      this.isLoading.set(false);
      throw error;
    }
  }

  /**
   * Download a file from the specified service
   * @param service The service endpoint
   * @param host The host to use (primary or secondary)
   * @returns A promise with the response
   */
  public async downloadFile(service: string, host = this.config.defaultHost) {
    this.isLoading.set(true);

    try {
      const url = this.getHostUrl(host) + '/' + service;

      const response$ = this.httpClient
        .get(url, {
          responseType: 'blob',
          observe: 'response',
        })
        .pipe(catchError(this.handleBlobError.bind(this)));

      const result = await lastValueFrom(response$);
      this.isLoading.set(false);
      return result;
    } catch (error) {
      this.isLoading.set(false);
      throw error;
    }
  }

  /**
   * Upload a file to the specified service and get the response as a blob
   * @param service The service endpoint
   * @param data The data to send
   * @param host The host to use (primary or secondary)
   * @returns A promise with the response
   */
  public async postFileAndGetBlob(service: string, data: any, host = this.config.defaultHost) {
    this.isLoading.set(true);

    try {
      let url = this.getHostUrl(host);
      if (service) {
        url += '/' + service;
      }

      const response$ = this.httpClient
        .post(url, data, {
          observe: 'response',
          responseType: 'blob',
        })
        .pipe(catchError(this.handleBlobError.bind(this)));

      const result = await lastValueFrom(response$);
      this.isLoading.set(false);
      return result;
    } catch (error) {
      this.isLoading.set(false);
      throw error;
    }
  }

  /**
   * Get a file from the specified service as an observable
   * @param service The service endpoint
   * @param host The host to use (primary or secondary)
   * @returns An observable with the response
   */
  public getFileObservable(service: string, host = this.config.defaultHost): Observable<Blob> {
    const url = this.getHostUrl(host) + '/' + service;
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  /**
   * Get the URL for the specified host
   * @param host The host to use (primary or secondary)
   * @returns The URL for the specified host
   */
  private getHostUrl(host: 'primary' | 'secondary' = this.config.defaultHost): string {
    return host === 'secondary' && this.config.secondaryUrl ? this.config.secondaryUrl : this.config.primaryUrl;
  }

  /**
   * Handle an error from a request
   * @param error The error to handle
   * @returns An observable that throws the error
   */
  private handleRequestError(error: HttpErrorResponse): Observable<never> {
    this.isLoading.set(false);

    // Set the last error
    if (error.error && typeof error.error === 'object') {
      this.lastError.set({
        error_message: error.error.error_message || 'An error occurred',
        explanation: error.error.explanation || 'Please try again later',
        status: error.status,
        code: error.error.code,
      });
    } else {
      this.lastError.set({
        error_message: error.message || 'An error occurred',
        explanation: 'Please try again later',
        status: error.status,
      });
    }

    // Call the custom error handler if provided
    if (this.config.errorHandler) {
      this.config.errorHandler(error);
    }

    return throwError(() => error);
  }

  /**
   * Handle an error from a blob request
   * @param error The error to handle
   * @returns An observable that throws the error
   */
  private async handleBlobError(error: HttpErrorResponse): Promise<Observable<never>> {
    // Convert blob error response to JSON if possible
    if (error.error instanceof Blob) {
      try {
        const errorText = await error.error.text();
        (error as any).error = JSON.parse(errorText);
      } catch (e) {
        // If parsing fails, create a generic error
        (error as any).error = {
          error_message: 'Error processing file',
          explanation: 'Unable to process server response',
        };
      }
    }

    return this.handleRequestError(error);
  }

  /**
   * Check the status of a response
   * @param response The response to check
   */
  private checkResponseStatus(response: any): void {
    // Implement any response status checking logic here
    // For example, check for specific status codes or error messages
  }

  /**
   * Convert an object to a plain object
   * @param obj The object to convert
   * @returns A plain object
   */
  private toPlainObject(obj: any): any {
    if (!obj) return obj;

    // Handle simple types
    if (typeof obj !== 'object' || obj instanceof Blob || obj instanceof File) {
      return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map((item) => this.toPlainObject(item));
    }

    // Handle objects
    const result: Record<string, any> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];

        // Skip functions and undefined values
        if (typeof value === 'function' || value === undefined) {
          continue;
        }

        result[key] = this.toPlainObject(value);
      }
    }

    return result;
  }
}
